package ro.horatiu_udrea.resursedeblocat

import androidx.compose.animation.*
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.*
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowLeft
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.automirrored.filled.OpenInNew
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.DragHandle
import androidx.compose.material.icons.filled.Person
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import coil3.compose.rememberAsyncImagePainter
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import resursedeblocat.composeapp.generated.resources.*
import kotlin.math.roundToInt

enum class SearchStep {
    CATEGORY, RESOURCE_TYPE, CONTEXT, LOADING, RESULT
}


data class Category(
    val name: String,
    val subcategories: List<String>
)

val categories = listOf(
    Category("Energie", listOf("Joacă", "Atenție", "Recuperare", "Alimentație", "Mișcare")),
    Category("Direcție", listOf("Rezonanță", "Măreție", "Sinestezie", "Claritate", "Ancorare")),
    Category("Evoluție", listOf("Prioritizare", "Căutare", "Învățare", "Practică", "Integrare")),
    Category("Mecanisme", listOf("Introspecție", "Conexiuni", "Flexibilizare", "Reziliență", "Adaptare")),
    Category("Influență", listOf("Servicii", "Prezentare", "Profil", "Publicare", "Parteneriate"))
)


@Composable
fun SearchResourceScreen(
    onBackToHome: () -> Unit,
    onResourceFound: (ResourceDetails) -> Unit,
    currentUser: User? = null,
    existingResources: List<ResourceDetails> = emptyList()
) {
    var currentStep by remember { mutableStateOf(SearchStep.CATEGORY) }
    var selectedSubcategory by remember { mutableStateOf<String?>(null) }
    var disabledSubcategories by remember { mutableStateOf(setOf<String>()) }

    LaunchedEffect(Unit) {
        val json = getRemoteConfigString("disabled_categories", "[]")
        try {
            // Simple JSON array parser for strings: ["A", "B"]
            // Also handle spaces and different quote types if any
            val parsed = json.trim()
                .removePrefix("[")
                .removeSuffix("]")
                .split(",")
                .map { it.trim().trim('\"').trim('\'').trim() }
                .filter { it.isNotEmpty() }
                .toSet()
            disabledSubcategories = parsed
        } catch (e: Exception) {
            println("Error parsing disabled categories: ${e.message}")
        }
    }

    var orderedResourceTypes by remember {
        mutableStateOf(
            listOf(
                "Quest Mindvalley",
                "Carte",
                "Curs",
                "Podcast",
                "Proces"
            )
        )
    }
    var contextText by remember { mutableStateOf("") }
    var resourceDetails by remember { mutableStateOf<ResourceDetails?>(null) }

    if (currentStep == SearchStep.RESULT) {
        resourceDetails?.let {
            ResourceDetailPage(
                details = it,
                onBack = {
                    onResourceFound(it)
                    onBackToHome()
                },
                showOptions = true,
                onStateChange = { newState ->
                    val user = currentUser
                    if (user != null) {
                        updateResourceState(
                            userId = user.id,
                            resourceId = it.id,
                            newState = newState,
                            onSuccess = {
                                println("Resource state updated successfully in Firestore for ${it.id}")
                                val updated = it.copy(state = newState)
                                resourceDetails = updated
                                // Also notify the caller about the update immediately if it's an existing resource
                                onResourceFound(updated)
                            },
                            onFailure = { error ->
                                println("Error updating resource state for ${it.id}: $error")
                                // Still update local state so UI is responsive, but maybe notify user?
                                val updated = it.copy(state = newState)
                                resourceDetails = updated
                                onResourceFound(updated)
                            }
                        )
                    } else {
                        val updated = it.copy(state = newState)
                        resourceDetails = updated
                        onResourceFound(updated)
                    }
                }
            )
        } ?: Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            CircularProgressIndicator(color = ThemePrimary)
        }
    } else {
        val scrollState = rememberScrollState()

        LaunchedEffect(currentStep) {
            scrollState.animateScrollTo(0)
        }

        BaseScreenLayout(
            scrollState = scrollState
        ) {
            Column(
                modifier = Modifier.padding(4.dp).fillMaxWidth()
            ) {
                // Segmented Progress Bar
                if (currentStep != SearchStep.RESULT) {
                    SegmentedProgressBar(currentStep = currentStep)
                    Spacer(modifier = Modifier.height(32.dp))
                }

                // Content
                Crossfade(targetState = currentStep, modifier = Modifier.fillMaxWidth()) { step ->
                    when (step) {
                        SearchStep.CATEGORY -> {
                            CategorySelectionStep(
                                selectedSubcategory = selectedSubcategory,
                                onSubcategorySelected = { sub ->
                                    selectedSubcategory = sub
                                },
                                disabledSubcategories = disabledSubcategories
                            )
                        }

                        SearchStep.RESOURCE_TYPE -> {
                            ResourceTypeSelectionStep(
                                orderedTypes = orderedResourceTypes,
                                onOrderChanged = { newList ->
                                    orderedResourceTypes = newList
                                }
                            )
                        }

                        SearchStep.CONTEXT -> {
                            ContextSelectionStep(
                                context = contextText,
                                onContextChanged = { contextText = it }
                            )
                        }

                        SearchStep.LOADING -> {
                            LaunchedEffect(Unit) {
                                recommendResource(
                                    subcategory = selectedSubcategory ?: "General",
                                    context = contextText,
                                    resourceTypes = orderedResourceTypes,
                                    onSuccess = { newResource ->
                                        resourceDetails = newResource
                                        currentStep = SearchStep.RESULT
                                    },
                                    onFailure = { error ->
                                        println("Error calling recommendResource: $error")
                                        // Return to context step to allow retry or showing error
                                        currentStep = SearchStep.CONTEXT
                                    }
                                )
                            }
                            LoadingStep()
                        }

                        else -> {}
                    }
                }

                Spacer(modifier = Modifier.height(32.dp))

                // Bottom Navigation
                if (currentStep != SearchStep.LOADING) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Button(
                            onClick = {
                                if (currentStep == SearchStep.CATEGORY) {
                                    onBackToHome()
                                } else {
                                    currentStep = SearchStep.entries[currentStep.ordinal - 1]
                                }
                            },
                            colors = ButtonDefaults.textButtonColors(
                                containerColor = Color.White,
                                contentColor = ThemePrimary
                            ),
                            elevation = null,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.height(45.dp),
                            contentPadding = PaddingValues(horizontal = 16.dp)
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(
                                    imageVector = Icons.AutoMirrored.Filled.KeyboardArrowLeft,
                                    contentDescription = null,
                                    modifier = Modifier.size(18.dp)
                                )
                                Spacer(modifier = Modifier.width(4.dp))
                                Text(stringResource(Res.string.search_back), style = MaterialTheme.typography.labelLarge)
                            }
                        }

                        if (currentStep != SearchStep.CONTEXT) {
                            Button(
                                onClick = {
                                    if (currentStep == SearchStep.CATEGORY) {
                                        val existing = existingResources.find {
                                            it.sourceSubcategory == selectedSubcategory && (it.state == ResourceState.FRESH || it.state == ResourceState.OBTAINED)
                                        }
                                        if (existing != null) {
                                            resourceDetails = existing
                                            currentStep = SearchStep.RESULT
                                        } else {
                                            currentStep = SearchStep.entries[currentStep.ordinal + 1]
                                        }
                                    } else {
                                        currentStep = SearchStep.entries[currentStep.ordinal + 1]
                                    }
                                },
                                enabled = (currentStep == SearchStep.CATEGORY && selectedSubcategory != null) || currentStep != SearchStep.CATEGORY,
                                colors = ButtonDefaults.buttonColors(containerColor = ThemePrimary),
                                shape = RoundedCornerShape(8.dp),
                                modifier = Modifier.height(45.dp)
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Text(stringResource(Res.string.search_next), style = MaterialTheme.typography.labelLarge)
                                    Spacer(modifier = Modifier.width(4.dp))
                                    Icon(
                                        imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                                        contentDescription = null,
                                        modifier = Modifier.size(18.dp)
                                    )
                                }
                            }
                        } else {
                            Button(
                                onClick = {
                                    currentStep = SearchStep.LOADING
                                },
                                enabled = contextText.isNotBlank(),
                                colors = ButtonDefaults.buttonColors(containerColor = ThemePrimary),
                                shape = RoundedCornerShape(8.dp),
                                modifier = Modifier.height(45.dp)
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Text(stringResource(Res.string.search_finish), style = MaterialTheme.typography.labelLarge)
                                    Spacer(modifier = Modifier.width(4.dp))
                                    Icon(
                                        imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                                        contentDescription = null,
                                        modifier = Modifier.size(18.dp)
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SegmentedProgressBar(currentStep: SearchStep) {
    val steps = listOf(
        stringResource(Res.string.search_step_category),
        stringResource(Res.string.search_step_type),
        stringResource(Res.string.search_step_context)
    )
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        steps.forEachIndexed { index, title ->
            val isActive = index <= currentStep.ordinal
            Column(modifier = Modifier.weight(1f)) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(6.dp)
                        .clip(RoundedCornerShape(3.dp))
                        .background(if (isActive) ThemePrimary else Color.LightGray.copy(alpha = 0.5f))
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = title,
                    style = MaterialTheme.typography.labelSmall,
                    color = if (isActive) ThemePrimary else Color.Gray,
                    fontWeight = if (isActive) FontWeight.Bold else FontWeight.Normal
                )
            }
        }
    }
}

@Composable
fun ResourceTypeSelectionStep(
    orderedTypes: List<String>,
    onOrderChanged: (List<String>) -> Unit
) {
    val list = remember { mutableStateListOf<String>() }
    LaunchedEffect(orderedTypes) {
        if (list != orderedTypes) {
            list.clear()
            list.addAll(orderedTypes)
        }
    }

    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = stringResource(Res.string.search_choose_type_title),
            style = MaterialTheme.typography.headlineMedium,
            color = ThemeTextDark
        )
        Text(
            text = stringResource(Res.string.search_choose_type_subtitle),
            style = MaterialTheme.typography.bodyMedium,
            color = GeneralText.copy(alpha = 0.7f)
        )

        Spacer(modifier = Modifier.height(24.dp))

        Box(modifier = Modifier.fillMaxWidth()) {
            val itemHeight = 72.dp
            val density = androidx.compose.ui.platform.LocalDensity.current
            val itemHeightPx = with(density) { itemHeight.toPx() }
            val spacingPx = with(density) { 12.dp.toPx() }
            val fullItemHeightPx = itemHeightPx + spacingPx

            var draggedItemKey by remember { mutableStateOf<String?>(null) }
            var dragOffset by remember { mutableStateOf(0f) }

            // Using a Box to allow manual positioning during drag
            Box(modifier = Modifier.fillMaxWidth().height(with(density) { (fullItemHeightPx * list.size).toDp() })) {
                list.forEachIndexed { index, type ->
                    key(type) {
                        val isDragging = draggedItemKey == type

                        val draggedIndex = draggedItemKey?.let { key -> list.indexOf(key) }

                        // Calculate where this item should be based on dragging
                        val targetIdx = if (draggedIndex != null && draggedIndex != -1) {
                            val dragTargetIdx = (draggedIndex + (dragOffset / fullItemHeightPx).roundToInt())
                                .coerceIn(0, list.size - 1)

                            if (isDragging) {
                                dragTargetIdx
                            } else if (index in (draggedIndex + 1)..dragTargetIdx) {
                                index - 1
                            } else if (index in dragTargetIdx until draggedIndex) {
                                index + 1
                            } else {
                                index
                            }
                        } else {
                            index
                        }

                        val animatedYOffset by animateFloatAsState(
                            targetValue = if (isDragging) index * fullItemHeightPx + dragOffset else targetIdx * fullItemHeightPx,
                            animationSpec = if (isDragging) androidx.compose.animation.core.snap() else androidx.compose.animation.core.spring()
                        )

                        Surface(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(itemHeight)
                                .zIndex(if (isDragging) 1f else 0f)
                                .graphicsLayer {
                                    translationY = animatedYOffset
                                }
                                .clip(RoundedCornerShape(12.dp)),
                            color = Color.White,
                            shape = RoundedCornerShape(12.dp),
                            border = if (isDragging) BorderStroke(
                                2.dp,
                                ThemePrimary
                            ) else null,
                        ) {
                            Row(
                                modifier = Modifier.fillMaxSize().padding(horizontal = 20.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Surface(
                                        color = ThemePrimary.copy(alpha = 0.1f),
                                        shape = RoundedCornerShape(8.dp),
                                        modifier = Modifier.size(36.dp)
                                    ) {
                                        Box(contentAlignment = Alignment.Center) {
                                            Text(
                                                text = (targetIdx + 1).toString(),
                                                style = MaterialTheme.typography.titleMedium,
                                                color = ThemePrimary,
                                                fontWeight = FontWeight.Bold
                                            )
                                        }
                                    }
                                    Spacer(modifier = Modifier.width(16.dp))
                                    Text(
                                        text = type,
                                        style = MaterialTheme.typography.titleMedium,
                                        color = ThemeTextDark,
                                        fontWeight = FontWeight.SemiBold
                                    )
                                }

                                Icon(
                                    imageVector = Icons.Default.DragHandle,
                                    contentDescription = stringResource(Res.string.search_drag_handle_content_description),
                                    tint = if (isDragging) ThemePrimary else Color.Gray.copy(alpha = 0.5f),
                                    modifier = Modifier.pointerInput(type) {
                                        detectDragGestures(
                                            onDragStart = {
                                                draggedItemKey = type
                                                dragOffset = 0f
                                            },
                                            onDrag = { change, dragAmount ->
                                                change.consume()
                                                dragOffset += dragAmount.y
                                            },
                                            onDragEnd = {
                                                val currentIndex = list.indexOf(type)
                                                if (currentIndex != -1) {
                                                    val finalTargetIndex =
                                                        (currentIndex + (dragOffset / fullItemHeightPx).roundToInt())
                                                            .coerceIn(0, list.size - 1)
                                                    if (finalTargetIndex != currentIndex) {
                                                        // Update local state first to avoid jumping
                                                        val item = list.removeAt(currentIndex)
                                                        list.add(finalTargetIndex, item)
                                                        onOrderChanged(list.toList())
                                                    }
                                                }
                                                draggedItemKey = null
                                                dragOffset = 0f
                                            },
                                            onDragCancel = {
                                                draggedItemKey = null
                                                dragOffset = 0f
                                            }
                                        )
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}


@Composable
fun CategorySelectionStep(
    selectedSubcategory: String?,
    onSubcategorySelected: (String) -> Unit,
    disabledSubcategories: Set<String> = emptySet()
) {
    var expandedCategory by remember { mutableStateOf<String?>(null) }
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = stringResource(Res.string.search_choose_category_title),
            style = MaterialTheme.typography.headlineMedium,
            color = ThemeTextDark
        )
        Text(
            text = stringResource(Res.string.search_choose_category_subtitle),
            style = MaterialTheme.typography.bodyMedium,
            color = GeneralText.copy(alpha = 0.7f)
        )

        Spacer(modifier = Modifier.height(24.dp))

        Column(
            verticalArrangement = Arrangement.spacedBy(12.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            categories.forEach { category ->
                AccordionItem(
                    category = category,
                    isExpanded = expandedCategory == category.name,
                    onToggle = {
                        expandedCategory = if (expandedCategory == category.name) null else category.name
                    },
                    selectedSubcategory = selectedSubcategory,
                    onSubcategorySelected = onSubcategorySelected,
                    disabledSubcategories = disabledSubcategories
                )
            }
        }
    }
}

@Composable
fun AccordionItem(
    category: Category,
    isExpanded: Boolean,
    onToggle: () -> Unit,
    selectedSubcategory: String?,
    onSubcategorySelected: (String) -> Unit,
    disabledSubcategories: Set<String> = emptySet()
) {
    val rotationState by animateFloatAsState(targetValue = if (isExpanded) 180f else 0f)

    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .clickable(
                onClick = onToggle
            ),
        color = Color.White,
        shadowElevation = 1.dp,
        shape = RoundedCornerShape(12.dp)
    ) {
        Column(modifier = Modifier.fillMaxWidth().padding(16.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(
                    text = category.name,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    color = ThemeTextDark
                )
                Icon(
                    imageVector = Icons.Default.ArrowDropDown,
                    contentDescription = null,
                    modifier = Modifier.rotate(rotationState),
                    tint = ThemePrimary
                )
            }

            AnimatedVisibility(
                visible = isExpanded,
                enter = expandVertically() + fadeIn(),
                exit = shrinkVertically() + fadeOut()
            ) {
                Column {
                    Spacer(modifier = Modifier.height(16.dp))
                    FlowRow(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        category.subcategories.forEach { sub ->
                            val isSelected = selectedSubcategory == sub
                            val isDisabled = disabledSubcategories.contains(sub)

                            Surface(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(20.dp))
                                    .then(
                                        if (isDisabled) Modifier else Modifier.clickable(
                                        onClick = { onSubcategorySelected(sub) }
                                    )),
                                color = when {
                                    isSelected -> ThemePrimary
                                    isDisabled -> Color.LightGray.copy(alpha = 0.3f)
                                    else -> Color.Transparent
                                },
                                shape = RoundedCornerShape(20.dp),
                                border = if (isSelected || isDisabled) null else androidx.compose.foundation.BorderStroke(
                                    1.dp,
                                    ThemePrimary.copy(alpha = 0.3f)
                                )
                            ) {
                                Text(
                                    text = sub,
                                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp),
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = when {
                                        isSelected -> Color.White
                                        isDisabled -> GeneralText.copy(alpha = 0.4f)
                                        else -> ThemeTextDark
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun FlowRow(
    modifier: Modifier = Modifier,
    horizontalArrangement: Arrangement.Horizontal = Arrangement.Start,
    verticalArrangement: Arrangement.Vertical = Arrangement.Top,
    content: @Composable FlowRowScope.() -> Unit
) {
    androidx.compose.foundation.layout.FlowRow(
        modifier = modifier,
        horizontalArrangement = horizontalArrangement,
        verticalArrangement = verticalArrangement,
        content = content
    )
}

@Composable
fun ContextSelectionStep(
    context: String,
    onContextChanged: (String) -> Unit
) {
    val focusRequester = remember { FocusRequester() }

    LaunchedEffect(Unit) {
        kotlinx.coroutines.delay(300) // Wait for scroll animation to start/finish
        focusRequester.requestFocus()
    }

    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            text = stringResource(Res.string.search_context_title),
            style = MaterialTheme.typography.headlineMedium,
            color = ThemeTextDark
        )
        Text(
            text = stringResource(Res.string.search_context_subtitle),
            style = MaterialTheme.typography.bodyMedium,
            color = GeneralText.copy(alpha = 0.7f)
        )

        Spacer(modifier = Modifier.height(24.dp))

        OutlinedTextField(
            value = context,
            onValueChange = onContextChanged,
            modifier = Modifier.fillMaxWidth().heightIn(min = 200.dp).focusRequester(focusRequester),
            placeholder = {
                Text(
                    stringResource(Res.string.search_context_placeholder),
                    style = MaterialTheme.typography.bodyLarge,
                    color = GeneralText.copy(alpha = 0.5f)
                )
            },
            shape = RoundedCornerShape(12.dp),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = ThemePrimary,
                unfocusedBorderColor = GeneralText.copy(alpha = 0.2f),
                cursorColor = ThemePrimary
            ),
            textStyle = MaterialTheme.typography.bodyLarge.copy(color = ThemeTextDark)
        )

        Spacer(modifier = Modifier.height(16.dp))

        if (stringResource(Res.string.search_context_hint).isNotEmpty()) {
            Text(
                text = stringResource(Res.string.search_context_hint),
                style = MaterialTheme.typography.bodySmall,
                color = GeneralText.copy(alpha = 0.6f)
            )
        }
    }
}

@Composable
fun LoadingStep() {
    var showSecondMessage by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        kotlinx.coroutines.delay(3000)
        showSecondMessage = true
    }

    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        CircularProgressIndicator(
            color = ThemePrimary,
            modifier = Modifier.size(64.dp),
            strokeWidth = 6.dp
        )
        Spacer(modifier = Modifier.height(24.dp))
        Crossfade(
            targetState = showSecondMessage,
            animationSpec = tween(durationMillis = 500),
            modifier = Modifier.fillMaxWidth()
        ) { isSecond ->
            val text = if (!isSecond) {
                stringResource(Res.string.ai_searching)
            } else {
                stringResource(Res.string.ai_analyzing)
            }
            Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                Text(
                    text = text,
                    style = MaterialTheme.typography.headlineSmall,
                    color = ThemeTextDark,
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
            }
        }
    }
}

@Composable
fun ResourceDetailPage(
    details: ResourceDetails,
    onBack: (() -> Unit)? = null,
    showOptions: Boolean = false,
    onStateChange: ((ResourceState) -> Unit)? = null,
    onDelete: (() -> Unit)? = null
) {
    val scrollState = rememberScrollState()

    LaunchedEffect(details.id) {
        scrollState.animateScrollTo(0)
    }

    BaseScreenLayout(
        scrollState = scrollState,
        footer = {
            if (onDelete != null) {
                Spacer(modifier = Modifier.height(24.dp))
                Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                    TextButton(
                        onClick = onDelete,
                        modifier = Modifier.padding(bottom = 8.dp)
                    ) {
                        Text(
                            text = stringResource(Res.string.delete_resource),
                            style = MaterialTheme.typography.labelLarge,
                            color = MaterialTheme.colorScheme.error
                        )
                    }
                }
            }
        }
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            val isWide = maxWidth > 500.dp
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 16.dp)
            ) {
                if (onBack != null || showOptions) {
                    val backButton = @Composable {
                        if (onBack != null) {
                            IconButton(onClick = onBack) {
                                Icon(
                                    imageVector = Icons.AutoMirrored.Filled.KeyboardArrowLeft,
                                    contentDescription = stringResource(Res.string.track_back),
                                    tint = ThemePrimary,
                                    modifier = Modifier.size(32.dp)
                                )
                            }
                        }
                    }

                    if (isWide) {
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
                            horizontalArrangement = Arrangement.Start,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            backButton()
                        }
                    } else {
                        Column(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
                            verticalArrangement = Arrangement.spacedBy(16.dp),
                            horizontalAlignment = Alignment.Start
                        ) {
                            backButton()
                        }
                    }
                }

                // Header cu titlu și tip
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(containerColor = ThemePrimary.copy(alpha = 0.05f)),
                    shape = RoundedCornerShape(16.dp),
                    border = BorderStroke(1.dp, ThemePrimary.copy(alpha = 0.1f))
                ) {
                    BoxWithConstraints(modifier = Modifier.padding(20.dp)) {
                        val isWideHeader = maxWidth > 500.dp
                        val content = @Composable { columnModifier: Modifier ->
                            Column(
                                modifier = columnModifier,
                                horizontalAlignment = if (isWideHeader) Alignment.Start else Alignment.CenterHorizontally
                            ) {

                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    Text(
                                        text = details.tip,
                                        style = MaterialTheme.typography.labelMedium,
                                        color = ThemePrimary,
                                        modifier = Modifier
                                            .padding(horizontal = 8.dp)
                                            .background(ThemePrimary.copy(alpha = 0.1f), RoundedCornerShape(4.dp))
                                            .padding(horizontal = 8.dp, vertical = 4.dp)
                                    )
                                    StateChip(
                                        state = details.state,
                                        textStyle = MaterialTheme.typography.labelMedium,
                                        verticalPadding = 4.dp
                                    )
                                }

                                Spacer(modifier = Modifier.height(8.dp))


                                Text(
                                    text = details.nume,
                                    style = MaterialTheme.typography.headlineMedium,
                                    color = ThemeTextDark,
                                    fontWeight = FontWeight.Bold,
                                    modifier = Modifier.padding(horizontal = 8.dp),
                                    textAlign = if (isWideHeader) androidx.compose.ui.text.style.TextAlign.Start else androidx.compose.ui.text.style.TextAlign.Center
                                )

                                Text(
                                    text = "de ${details.autor}",
                                    style = MaterialTheme.typography.titleMedium,
                                    color = ThemeTextDark.copy(alpha = 0.7f),
                                    modifier = Modifier.padding(horizontal = 8.dp),
                                    textAlign = if (isWideHeader) androidx.compose.ui.text.style.TextAlign.Start else androidx.compose.ui.text.style.TextAlign.Center
                                )

                                if (details.link.isNotBlank()) {
                                    Spacer(modifier = Modifier.height(12.dp))
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        modifier = Modifier
                                            .clip(RoundedCornerShape(8.dp))
                                            .clickable { openUrl(details.link) }
                                            .padding(horizontal = 8.dp, vertical = 4.dp)
                                    ) {
                                        Text(
                                            text = stringResource(Res.string.access_resource),
                                            style = MaterialTheme.typography.labelLarge,
                                            color = Color(0xFF0066CC),
                                            fontWeight = FontWeight.Bold
                                        )
                                        Spacer(modifier = Modifier.width(4.dp))
                                        Icon(
                                            imageVector = Icons.AutoMirrored.Filled.OpenInNew,
                                            contentDescription = null,
                                            tint = Color(0xFF0066CC),
                                            modifier = Modifier.size(16.dp)
                                        )
                                    }
                                }
                            }
                        }

                        val image = @Composable {
                            if (!details.imagine.isNullOrBlank()) {
                                Spacer(modifier = if (isWideHeader) Modifier.width(20.dp) else Modifier.height(20.dp))
                                Surface(
                                    modifier = Modifier
                                        .widthIn(max = if (isWideHeader) 150.dp else 200.dp)
                                        .clickable {
                                            if (details.link.isNotBlank()) {
                                                openUrl(details.link)
                                            }
                                        }
                                        .clip(RoundedCornerShape(12.dp)),
                                    color = Color.LightGray.copy(alpha = 0.3f)
                                ) {
                                    Image(
                                        painter = rememberAsyncImagePainter(model = details.imagine),
                                        contentDescription = "Imagine resursă",
                                        contentScale = androidx.compose.ui.layout.ContentScale.Fit,
                                        modifier = Modifier.wrapContentSize()
                                    )
                                }
                            }
                        }

                        if (isWideHeader) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                content(Modifier.weight(1f))
                                image()
                            }
                        } else {
                            Column(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                content(Modifier.fillMaxWidth())
                                image()
                            }
                        }
                    }
                }

                // Message based on state
                val stateMessage = when (details.state) {
                    ResourceState.NOT_OBTAINED -> stringResource(Res.string.msg_not_obtained)
                    ResourceState.FINISHED -> stringResource(Res.string.msg_finished)
                    ResourceState.ABANDONED -> stringResource(Res.string.msg_abandoned)
                    ResourceState.OBTAINED -> stringResource(Res.string.msg_obtained)
                    else -> null
                }

                stateMessage?.let {
                    Card(
                        modifier = Modifier.fillMaxWidth().padding(top = 16.dp, bottom = 16.dp),
                        colors = CardDefaults.cardColors(containerColor = ThemePrimary.copy(alpha = 0.1f)),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Text(
                            text = it,
                            modifier = Modifier.padding(16.dp),
                            style = MaterialTheme.typography.bodyMedium,
                            color = ThemePrimary,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }

                // State buttons
                if (showOptions && onStateChange != null) {
                    val arrangement = Arrangement.spacedBy(8.dp)

                    if (isWide) {
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(top = 12.dp, bottom = 16.dp),
                            horizontalArrangement = Arrangement.Center
                        ) {
                            StateButtonsContent(details, onStateChange, isWide)
                        }
                    } else {
                        Column(
                            modifier = Modifier.fillMaxWidth().padding(top = 12.dp, bottom = 16.dp),
                            verticalArrangement = arrangement,
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            StateButtonsContent(details, onStateChange, isWide)
                        }
                    }
                }

                Spacer(modifier = Modifier.height(8.dp))

                // Categorii ca Chips
                Text(
                    text = stringResource(Res.string.other_categories),
                    style = MaterialTheme.typography.titleSmall,
                    color = ThemePrimary,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(8.dp))
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    details.categorii_match.forEach { categorie ->
                        Surface(
                            color = ThemePrimary.copy(alpha = 0.08f),
                            shape = RoundedCornerShape(16.dp),
                            border = BorderStroke(1.dp, ThemePrimary.copy(alpha = 0.2f))
                        ) {
                            Text(
                                text = categorie,
                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                                style = MaterialTheme.typography.bodySmall,
                                color = ThemePrimary,
                                fontWeight = FontWeight.Medium
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.height(24.dp))

                ResultSection(stringResource(Res.string.about_resource), details.despre_resursa)
                ResultSection(stringResource(Res.string.about_author), details.despre_autor)
                details.sugestii?.let {
                    if (it.isNotBlank()) {
                        ResultSection(stringResource(Res.string.usage_suggestions), it)
                    }
                }

                if (details.parcurs_personal.isNotBlank() || details.recomandat_daca.isNotBlank()) {
                    Card(
                        modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                        colors = CardDefaults.cardColors(containerColor = ThemePrimary.copy(alpha = 0.05f)),
                        shape = RoundedCornerShape(12.dp),
                        border = BorderStroke(1.dp, ThemePrimary.copy(alpha = 0.1f))
                    ) {
                        Column(modifier = Modifier.padding(16.dp)) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(
                                    imageVector = Icons.Default.Person,
                                    contentDescription = null,
                                    tint = ThemePrimary,
                                    modifier = Modifier.size(20.dp)
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text(
                                    text = "Hori",
                                    style = MaterialTheme.typography.labelLarge,
                                    color = ThemePrimary,
                                    fontWeight = FontWeight.Bold
                                )
                            }

                            if (details.parcurs_personal.isNotBlank()) {
                                Spacer(modifier = Modifier.height(12.dp))
                                Text(
                                    text = stringResource(Res.string.personal_journey),
                                    style = MaterialTheme.typography.labelMedium,
                                    color = ThemePrimary,
                                    fontWeight = FontWeight.Bold
                                )
                                Spacer(modifier = Modifier.height(4.dp))
                                Text(
                                    text = details.parcurs_personal,
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = ThemeTextDark
                                )
                            }

                            if (details.recomandat_daca.isNotBlank()) {
                                Spacer(modifier = Modifier.height(12.dp))
                                Text(
                                    text = stringResource(Res.string.recommend_if),
                                    style = MaterialTheme.typography.labelMedium,
                                    color = ThemePrimary,
                                    fontWeight = FontWeight.Bold
                                )
                                Spacer(modifier = Modifier.height(4.dp))
                                Text(
                                    text = details.recomandat_daca,
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = ThemeTextDark
                                )
                            }
                        }
                    }
                }

            }
        }
    }
}

@Composable
private fun StateButtonsContent(
    details: ResourceDetails,
    onStateChange: (ResourceState) -> Unit,
    isWide: Boolean
) {
    val modifier = if (isWide) Modifier.padding(horizontal = 8.dp).height(36.dp) else Modifier.fillMaxWidth().padding(vertical = 4.dp).height(48.dp)
    when (details.state) {
        ResourceState.FRESH -> {
            Button(
                onClick = { onStateChange(ResourceState.OBTAINED) },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF4CAF50)),
                shape = RoundedCornerShape(8.dp),
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                modifier = modifier
            ) {
                Text(stringResource(Res.string.option_obtained), style = MaterialTheme.typography.labelMedium)
            }
            Button(
                onClick = { onStateChange(ResourceState.NOT_OBTAINED) },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFF44336)),
                shape = RoundedCornerShape(8.dp),
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                modifier = modifier
            ) {
                Text(stringResource(Res.string.option_not_obtained), style = MaterialTheme.typography.labelMedium)
            }
        }

        ResourceState.OBTAINED -> {
            Button(
                onClick = { onStateChange(ResourceState.FINISHED) },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF4CAF50)),
                shape = RoundedCornerShape(8.dp),
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                modifier = modifier
            ) {
                Text(stringResource(Res.string.option_finished), style = MaterialTheme.typography.labelMedium)
            }
            Button(
                onClick = { onStateChange(ResourceState.ABANDONED) },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFF44336)),
                shape = RoundedCornerShape(8.dp),
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                modifier = modifier
            ) {
                Text(stringResource(Res.string.option_abandoned), style = MaterialTheme.typography.labelMedium)
            }
        }

        else -> {}
    }
}

@Composable
fun ResultSection(title: String, content: String) {
    Column(modifier = Modifier.fillMaxWidth().padding(vertical = 12.dp)) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleSmall,
            color = ThemePrimary,
            fontWeight = FontWeight.Bold
        )
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = content,
            style = MaterialTheme.typography.bodyLarge,
            color = ThemeTextDark,
            lineHeight = androidx.compose.ui.unit.TextUnit.Unspecified
        )
        Spacer(modifier = Modifier.height(8.dp))
        HorizontalDivider(color = GeneralText.copy(alpha = 0.05f))
    }
}
